import * as Yup from 'yup';
import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { useParams, useNavigate } from 'react-router-dom';
import { useGetTableTextByKey, updateTableExplanation } from 'src/api/admin';

// MUI
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import Container from '@mui/material/Container';

// Routes & Locales
import { paths } from 'src/routes/paths';
import { useLocales } from 'src/locales';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

// Components
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFEditor, RHFTextField } from 'src/components/hook-form';

// Data & Types
import { ITableTextItem } from 'src/types/table-text';
import type { SubmitHandler } from 'react-hook-form';

export default function TableTextEditForm() {
  const { t } = useLocales('tableTextPage');
  const { enqueueSnackbar } = useSnackbar();
  const { key } = useParams();
  const navigate = useNavigate();

  const TableTextSchema = Yup.object().shape({
    key: Yup.string().required(),
    name: Yup.string().required(),
    content: Yup.string().required('Content is required'),
  });

  const methods = useForm<ITableTextItem>({
    resolver: yupResolver(TableTextSchema),
    defaultValues: {
      key: '',
      name: '',
      content: '',
    },
  });

  const {
    reset,
    formState: { isSubmitting },
  } = methods;

  const { data: tableTextData } = useGetTableTextByKey(key);

  useEffect(() => {
    if (tableTextData) {
      reset({
        key: tableTextData.key,
        name: tableTextData.name,
        content: tableTextData.content,
      });
    }
  }, [tableTextData, reset]);

  const onSubmit: SubmitHandler<ITableTextItem> = async (data) => {
    try {
      await updateTableExplanation({ key: data.key, content: data.content });
      enqueueSnackbar(t('saved_successfully'));
      navigate(paths.admin.dashboard.table_text);
    } catch (error) {
      enqueueSnackbar(t('save_error'), { variant: 'error' });
    }
  };

  return (
    <Container maxWidth="xl">
      <CustomBreadcrumbs
        heading={t('edit')}
        links={[
          { name: t('admin') },
          { name: t('table_text'), href: paths.admin.dashboard.table_text },
          { name: t('edit') },
        ]}
        sx={{ mb: { xs: 3, md: 5 } }}
      />
      {tableTextData?.name && (
        <Typography variant="h4" sx={{ mb: 2 }}>
          {tableTextData.name}
        </Typography>
      )}

      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card>
              <Stack spacing={3} sx={{ p: 3 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2">{t('table_name')}</Typography>
                  <RHFTextField
                    name="name"
                    label={t('table_name')}
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2">{t('content')}</Typography>
                  <RHFEditor name="content" simple={false} />
                </Stack>
              </Stack>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <LoadingButton
              type="submit"
              variant="contained"
              size="medium"
              loading={isSubmitting}
              sx={{ ml: 2, mt: 3, width: '100px' }}
            >
              {t('save')}
            </LoadingButton>
          </Grid>
        </Grid>
      </FormProvider>
    </Container>
  );
}
